/* apollo-client.ts
 * -------------------------------------------------------------------------- */

import Vue from 'vue'
import VueApollo from 'vue-apollo'

import { HttpLink } from 'apollo-link-http'
import { ApolloLink, Observable } from 'apollo-link'
import { RetryLink } from 'apollo-link-retry'
import { onError } from 'apollo-link-error'
import { createApolloClient, restartWebsockets } from 'vue-cli-plugin-apollo/graphql-client'

// add module import and define apolloClient type
import { ApolloClient } from 'apollo-client'
import { SubscriptionClient } from 'subscriptions-transport-ws'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { Store } from 'vuex'
import router from '@/router'
import { State } from '@/store/state'
import store from '@/store'
import { sendError } from '@/utils/notificationError'
import { getErrorMessage } from '@/utils/translations'

// 👇 Type-guard import
import { OperationDefinitionNode } from 'graphql'

// Install the vue plugin
Vue.use(VueApollo)

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------
export const AUTH_TOKEN = 'apollo-token'
export const INVALID_JWT = 'invalid-jwt'
export const USER = 'unexpected'
export const RUT_ERROR = '412'
export const RUT_API = '503'
export const INVALID_RUT = 'BAD_USER_INPUT'

// ---------------------------------------------------------------------------
// Utils
// ---------------------------------------------------------------------------
const extractOperationName = (operationBody: string): string => {
  const match = operationBody.match(/\b(?:query|mutation)\s*(\w+)/)
  return match ? match[1] : 'Unnamed Operation'
}

/* -------------------------------------------------------------------------- */
/* Error handler link                                                         */
/* -------------------------------------------------------------------------- */
const errorHandlerLink = new ApolloLink((operation, forward) => {
  return forward(operation).map(response => {
    const context = operation.getContext()

    if (response?.errors?.length && !context.isTryingRoles) {
      for (const { message, extensions } of response.errors) {
        if (!extensions) continue

        const filters = JSON.stringify(operation.variables)
        const operationBody = operation.query.loc!.source.body
        const operationName = extractOperationName(operationBody)
        const currentRoute = router.currentRoute.fullPath
        const requestRoot = window.location.origin
        const fullUrl = `${requestRoot}${currentRoute}`

        const attemptedRole = context.attemptedRole || 'N/A'

        const error = {
          app_name: 'GENIO',
          view: fullUrl,
          record_id: `Datos usuario ${JSON.stringify(store.getters['user/user'])}`,
          context: `Rol: ${attemptedRole} | Message: ${message},  Extension Code: ${extensions.code}
                Query: ${operationName} Variables: ${filters}`,
        }

        switch (extensions.code) {
          case INVALID_JWT: throw new Error(INVALID_JWT)
          case RUT_ERROR: throw new Error(RUT_ERROR)
          case RUT_API: throw new Error(RUT_ERROR)
          case INVALID_RUT: throw new Error('Invalid RUT')

          case USER: {
            const msg = extensions?.internal?.error?.message || 'Contacta al soporte técnico'
            const text = getErrorMessage(msg || 'ERROR_CUSTOMER_BUSINESS_OPEN', msg) || msg
            if (text === 'Contacta al soporte técnico') sendError(error)

            window.dispatchEvent(
              new CustomEvent('notification-message', {
                detail: { message: text, type: 'error' },
              })
            )
            break
          }

          default:
            sendError(error)
            window.dispatchEvent(
              new CustomEvent('notification-message', {
                detail: { message: 'Contacta al soporte técnico', type: 'error' },
              })
            )
            // eslint-disable-next-line no-console
            console.log(`[GraphQL error]: Message: ${message},  Extension Code: ${extensions.code}`)
        }
      }
    }
    return response
  })
})

/* -------------------------------------------------------------------------- */
/* Roles helpers                                                              */
/* -------------------------------------------------------------------------- */
function getOrderedRoles (roles: any[]): string[] {
  const priorityOrder = [
    'staff', 'supervisor', 'validator', 'forwarder', 'appraiser',
    'inspector', 'treasurer', 'advertiser', 'sysop', 'financer', 'coo',
  ]
  const slugs = roles?.map(r => r.slug) || []

  return slugs.sort((a, b) => {
    const ia = priorityOrder.indexOf(a)
    const ib = priorityOrder.indexOf(b)
    if (ia === -1 && ib === -1) return 0
    if (ia === -1) return 1
    if (ib === -1) return -1
    return ia - ib
  })
}

export async function ensureRolesAreLoaded (store: typeof import('@/store').default, isMutation: boolean) {
  const roles = store.getters['user/roles']
  const currentPath = router.currentRoute.path

  /* ---------------- Queries ---------------- */
  if (!isMutation) {
    let ordered = getOrderedRoles(roles)

    if (currentPath.includes('_')) {
      const explicit = currentPath.split('/')
        .find(s => s.includes('_'))!
        .split('_')[0]
        .toLowerCase()

      ordered = [explicit, ...ordered.filter(r => r !== explicit)]
    }
    return ordered
  }

  /* ---------------- Mutations -------------- */
  if (currentPath.includes('_')) {
    const role = currentPath.split('/')
      .find(p => p.includes('_'))!
      .split('_')[0]
    return [role.toLowerCase()]
  }

  if (['person', 'schedule', 'stock', 'auto'].some(sub => currentPath.includes(sub))) {
    return getOrderedRoles(roles) || null
  }

  if (currentPath.includes('home')) {
    return roles?.filter(r => r.slug.toLowerCase() === 'supervisor')
      ?.map(r => r.slug) || null
  }

  return roles?.length ? roles.map(r => r.slug) : null
}

/* -------------------------------------------------------------------------- */
/* Auth helpers                                                               */
/* -------------------------------------------------------------------------- */
export async function ensureAuthReady (store: typeof import('@/store').default, isMutation = true) {
  const token = localStorage.getItem(AUTH_TOKEN)
  const admin = store.getters['user/defaultRole'] === 'admin'
  const roles = admin ? null : await ensureRolesAreLoaded(store, isMutation)
  return { token, roles }
}

/* -------------------------------------------------------------------------- */
/* Auth link                                                                  */
/* -------------------------------------------------------------------------- */
const auth = new ApolloLink((operation, forward) => {
  /* Type-guard para quedarnos con el OperationDefinitionNode ---------------- */
  const opDef = operation.query.definitions.find(
    (d): d is OperationDefinitionNode => d.kind === 'OperationDefinition'
  )

  const isMutation = opDef?.operation === 'mutation'

  /* Helper: intenta la request con todos los roles -------------------------- */
  const tryRequest = (
    observer: any,
    { token, roles, index = 0 }: { token: string | null, roles: (string | null)[], index?: number },
    accumulatedErrors: any[] = []
  ) => {
    const headers: Record<string, string> = { authorization: `Bearer ${token}` }
    if (roles[index]) headers['x-hasura-role'] = roles[index] as string

    /*  ✅ GUARDA EL ROL QUE SE ESTÁ INTENTANDO PARA LOS LINKS DE ERROR  */
    operation.setContext({
      headers,
      attemptedRole: roles[index] || null,
      isTryingRoles: roles.length > 1 && index < roles.length - 1,
    })

    forward(operation).subscribe({
      next: response => {
        if ('data' in response) {
          observer.next(response)
          observer.complete()
        } else if (index < roles.length - 1) {
          const newErrors = [...accumulatedErrors, ...(response.errors || [])]
          tryRequest(observer, { token, roles, index: index + 1 }, newErrors)
        } else {
          const msg = accumulatedErrors.map(e => e.message).join('; ')
          observer.error(new Error(
            `No se pudo completar la operación con ningún rol disponible. Errores: ${msg}`
          ))
        }
      },
      error: observer.error.bind(observer),
      complete: () => { if (index >= roles.length - 1) observer.complete() },
    })
  }

  /* Start auth flow --------------------------------------------------------- */
  return new Observable(observer => {
    ensureAuthReady(store, isMutation)
      .then(({ token, roles }) => {
        const initialRoles = roles?.length ? roles : [null]
        tryRequest(observer, { token, roles: initialRoles })
      })
      .catch(observer.error.bind(observer))
  })
})

export type VueApolloClient = ApolloClient<InMemoryCache> & {
  wsClient: SubscriptionClient
}

/* -------------------------------------------------------------------------- */
/* Error link                                                                 */
/* -------------------------------------------------------------------------- */
const errorLink = onError(({ graphQLErrors, networkError, forward, operation }) => {
  if (networkError && networkError.message.toLowerCase().includes('invalid-jwt')) {
    return new Observable(observer => {
      store.dispatch('user/refreshToken').then(() => {
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`,
          },
        }))
        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        })
      }).catch(error => {
        observer.error(error)

        const filters = JSON.stringify(operation.variables)
        const operationBody = operation.query.loc!.source.body
        const operationName = extractOperationName(operationBody)
        const currentRoute = router.currentRoute.fullPath
        const fullUrl = `${window.location.origin}${currentRoute}`
        const attemptedRole = operation.getContext().attemptedRole || 'N/A'

        if (!/such as timeout/i.test(error.message)) {
          sendError({
            app_name: 'GENIO',
            view: fullUrl,
            record_id: `Datos usuario ${JSON.stringify(store.getters['user/user'])}`,
            context: `Rol: ${attemptedRole} | ${JSON.stringify(error.message)} Query: ${operationName} Variables: ${filters}`,
          })

          window.dispatchEvent(
            new CustomEvent('notification-message', {
              detail: { message: `Contacta soporte tecnico mensaje: ${error.message}`, type: 'error' },
            })
          )
        }
      })
    })
  }

  const ctx = operation.getContext()
  if (graphQLErrors && !ctx.isTryingRoles) {
    graphQLErrors.forEach(err =>
      console.error(`[GraphQL error]: Message: ${err.message}, Location: ${err.locations}, Path: ${err.path}`)
    )
  } else {
    return forward(operation)
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`)
    window.dispatchEvent(
      new CustomEvent('notification-message', {
        detail: { message: `Error de red, verifica tu conexión: ${networkError.message}`, type: 'error' },
      })
    )
  }
})

/* -------------------------------------------------------------------------- */
/* Retry link                                                                 */
/* -------------------------------------------------------------------------- */
const retryLink = new RetryLink({
  attempts: (count, _op, error) => {
    const network = error.statusCode >= 500
    const tokenErr = error.message === INVALID_JWT && count < 3
    return tokenErr || (network && count < 3)
  },
  delay: count => count * 300,
})

/* -------------------------------------------------------------------------- */
/* HTTP link                                                                  */
/* -------------------------------------------------------------------------- */
const httpLink = new HttpLink({
  uri: process.env.VUE_APP_GRAPHQL_HTTP || 'http://localhost:4000/graphql',
  fetch,
})

/* -------------------------------------------------------------------------- */
/* Config provider                                                            */
/* -------------------------------------------------------------------------- */
const httpEndpoint =
  process.env.VUE_APP_GRAPHQL_HTTP || 'http://localhost:4000/graphql'

export const filesRoot =
  process.env.VUE_APP_FILES_ROOT ||
  httpEndpoint.substr(0, httpEndpoint.indexOf('/graphql'))

Vue.prototype.$filesRoot = filesRoot

const defaultOptions = {
  httpEndpoint,
  wsEndpoint: process.env.VUE_APP_GRAPHQL_WS || 'ws://localhost:4000/graphql',
  tokenName: AUTH_TOKEN,
  persisting: false,
  websocketsOnly: false,
  ssr: false,
  preAuthLinks: [auth, retryLink, errorLink, errorHandlerLink, httpLink],
}

/* -------------------------------------------------------------------------- */
/* Provider creation                                                          */
/* -------------------------------------------------------------------------- */
export function createProvider (options = {}): VueApollo {
  const { apolloClient, wsClient } = createApolloClient({
    ...defaultOptions,
    ...options,
  })
  // @ts-ignore
  apolloClient.wsClient = wsClient

  return new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: { $query: { fetchPolicy: 'cache-and-network' } },
    errorHandler (error) {
      // eslint-disable-next-line no-console
      console.log('%cError', 'background: red; color: white; padding:2px 4px; border-radius:3px; font-weight:bold;', error.message)
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Login / Logout helpers                                                     */
/* -------------------------------------------------------------------------- */
export async function onLogin (apolloClient: VueApolloClient, token: string): Promise<void> {
  if (typeof localStorage !== 'undefined' && token) localStorage.setItem(AUTH_TOKEN, token)
  if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient)
  try { await apolloClient.resetStore() } catch (e) {
    // eslint-disable-next-line no-console
    console.error('%cError on cache reset (login)', 'color: orange;', (e as Error).message)
  }
}

export async function onLogout (apolloClient: VueApolloClient): Promise<void> {
  if (typeof localStorage !== 'undefined') localStorage.removeItem(AUTH_TOKEN)
  if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient)
  try { await apolloClient.resetStore() } catch (e) {
    // eslint-disable-next-line no-console
    console.error('%cError on cache reset (logout)', 'color: orange;', (e as Error).message)
  }
}

/* -------------------------------------------------------------------------- */
/* Init apollo client in store                                                */
/* -------------------------------------------------------------------------- */
export function initApolloClient (store: Store<State>, provider: VueApollo): void {
  store.state.apolloClient = provider.defaultClient as VueApolloClient
}
